/**
 * Duas nocoes de tempo convivem no sistema e nunca devem se misturar:
 *
 * 1. INSTANTE  - um momento no tempo (quando a transacao foi criada).
 *    Persistido em UTC (DateTime no Prisma), exibido em America/Sao_Paulo.
 *
 * 2. DATA DE COMPETENCIA - um dia do calendario (vencimento do boleto,
 *    fechamento da fatura). NAO tem hora e NAO sofre conversao de fuso.
 *    Persistido como `@db.Date` e representado aqui por PlainDate: a string
 *    'YYYY-MM-DD'. Nunca use um Date do JS para isso, ou o fuso come um dia:
 *    new Date('2026-01-31') em UTC-3 imprime 30/01.
 */
export const APP_TIME_ZONE = 'America/Sao_Paulo';
export class DateError extends Error {
    constructor(message) {
        super(message);
        this.name = 'DateError';
    }
}
const PLAIN_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
export function isPlainDate(value) {
    if (typeof value !== 'string')
        return false;
    const match = PLAIN_DATE_RE.exec(value);
    if (!match)
        return false;
    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    if (month < 1 || month > 12)
        return false;
    if (day < 1 || day > daysInMonth(year, month))
        return false;
    return true;
}
/** Valida e marca uma string como PlainDate. */
export function plainDate(value) {
    if (!isPlainDate(value))
        throw new DateError(`Data invalida (esperado YYYY-MM-DD): ${value}`);
    return value;
}
export function makePlainDate(year, month, day) {
    const iso = `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return plainDate(iso);
}
export function daysInMonth(year, month) {
    return new Date(Date.UTC(year, month, 0)).getUTCDate();
}
export function partsOf(date) {
    const match = PLAIN_DATE_RE.exec(date);
    if (!match)
        throw new DateError(`Data invalida: ${date}`);
    return { year: Number(match[1]), month: Number(match[2]), day: Number(match[3]) };
}
/** Hoje no fuso da aplicacao, nao no fuso do servidor. */
export function todayInAppZone(now = new Date()) {
    return instantToPlainDate(now);
}
/** Converte um instante para o dia do calendario em America/Sao_Paulo. */
export function instantToPlainDate(instant, timeZone = APP_TIME_ZONE) {
    // 'en-CA' produz exatamente YYYY-MM-DD.
    const formatted = new Intl.DateTimeFormat('en-CA', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).format(instant);
    return plainDate(formatted);
}
/**
 * Converte uma data de competencia no instante UTC correspondente a meia-noite
 * em America/Sao_Paulo. Usado so na fronteira com o banco (`@db.Date`).
 */
export function plainDateToUtcMidnight(date) {
    const { year, month, day } = partsOf(date);
    return new Date(Date.UTC(year, month - 1, day));
}
/** 'YYYY-MM-DD' -> '31/01/2026'. Camada de apresentacao. */
export function formatPlainDateBR(date) {
    const { year, month, day } = partsOf(date);
    return `${String(day).padStart(2, '0')}/${String(month).padStart(2, '0')}/${year}`;
}
/** '31/01/2026' -> 'YYYY-MM-DD'. Entrada de formulario. */
export function parsePlainDateBR(input) {
    const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(input.trim());
    if (!match)
        throw new DateError(`Data invalida (esperado DD/MM/AAAA): ${input}`);
    return makePlainDate(Number(match[3]), Number(match[2]), Number(match[1]));
}
export function addDays(date, days) {
    const { year, month, day } = partsOf(date);
    const shifted = new Date(Date.UTC(year, month - 1, day + days));
    return makePlainDate(shifted.getUTCFullYear(), shifted.getUTCMonth() + 1, shifted.getUTCDate());
}
/**
 * Soma meses preservando o "dia pretendido" ate onde o mes permite.
 * 31/01 + 1 mes = 28/02 (ou 29 em bissexto), nao 03/03.
 * Essencial para recorrencias e para o ciclo de fatura do cartao.
 */
export function addMonths(date, months) {
    const { year, month, day } = partsOf(date);
    const totalMonths = year * 12 + (month - 1) + months;
    const targetYear = Math.floor(totalMonths / 12);
    const targetMonth = (totalMonths % 12) + 1;
    const clampedDay = Math.min(day, daysInMonth(targetYear, targetMonth));
    return makePlainDate(targetYear, targetMonth, clampedDay);
}
export function startOfMonth(date) {
    const { year, month } = partsOf(date);
    return makePlainDate(year, month, 1);
}
export function endOfMonth(date) {
    const { year, month } = partsOf(date);
    return makePlainDate(year, month, daysInMonth(year, month));
}
export function compareDates(a, b) {
    return a < b ? -1 : a > b ? 1 : 0;
}
export function differenceInDays(from, to) {
    const ms = plainDateToUtcMidnight(to).getTime() - plainDateToUtcMidnight(from).getTime();
    return Math.round(ms / 86400000);
}
/** 0 = domingo ... 6 = sabado. Usado no relatorio "quando gasto". */
export function dayOfWeek(date) {
    return plainDateToUtcMidnight(date).getUTCDay();
}
/** Formata um instante para exibicao em America/Sao_Paulo. */
export function formatInstantBR(instant, timeZone = APP_TIME_ZONE) {
    return new Intl.DateTimeFormat('pt-BR', {
        timeZone,
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    }).format(instant);
}
//# sourceMappingURL=index.js.map